import { Input } from "@components/Input";
import { Textarea } from "@components/Input/Textarea";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { FC, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useForm } from "react-hook-form";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useTranslation } from "@helpers/useTranslation";
import { FirebaseContext } from "src/helpers/firebase";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useToast } from "src/helpers/useToast";
import { Question } from "./Question";
import { BettingScenario } from "./BettingScenario";
import { Modal } from "@components/Modal";
import { ConfirmModal } from "@components/ConfirmModal";
import { OKModal } from "@components/OKModal";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { reorder } from "@helpers/reorder";
import { uid } from "@helpers/uid";
import { TrialDegrate } from "@components/TrialDegrate";
import { useSimpleCollection } from "@helpers/useSimpleCollection";
import { QuestionPhotoModal } from "./QuestionPhotoModal";
import { IntroPhotoModal } from "./IntroPhotoModal";
import { AudioModal } from "./AudioModal";

type Props = {
  close: () => void;
};

export const CreateModal: FC<Props> = ({ close }) => {
  const { t, i18n } = useTranslation("admin");
  const locale = (i18n.language || "en") as AppLocale;

  const {
    register,
    setValue,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
  } = useForm<any>();

  const [activeTab, setActiveTab] = useState<number | undefined>(undefined);
  const [showAutoTranslateConfirm, setShowAutoTranslateConfirm] = useState(false);
  const [activeQuestionPhotoIndex, setActiveQuestionPhotoIndex] = useState<number | undefined>(undefined);
  const [showIntroPhotoModal, setShowIntroPhotoModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [tempChallengeId] = useState(uid()); // Temporary ID for new challenge (for photo uploads)

  const { firestore, functions } = useContext(FirebaseContext);
  const [autoTranslateChallenge] = useHttpsCallable<
    API.AutoTranslateChallengeRequest,
    API.AutoTranslateChallengeResponse
  >(functions, "autoTranslateChallengeV2");
  const [suggestChallenge] = useHttpsCallable<API.SuggestChallengeRequest, API.SuggestChallengeResponse>(
    functions,
    "suggestChallengeV2",
  );
  const eventId = useEventId();
  const [updating, setUpdating] = useState(false);
  const toast = useToast();

  //https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  const [event] = useEvent();

  const { getKeyName } = useTranslatedKey();

  const onSubmit = async ({
    reward,
    type,
    title,
    about,
    copy,
    questions,
    scenarios,
    showCorrectAnswers,
    autoTranslate,
    introPhotoUri,
    audioUri,
  }) => {
    showCorrectAnswers = !!showCorrectAnswers;
    autoTranslate = autoTranslate !== false; // Default to true
    if (isTrial || !hasMultipleLanguages) autoTranslate = false; // Disable auto-translate in trial mode or single language events

    setUpdating(true);
    try {
      questions = (questions || []).map((question) => {
        const processedQuestion: any = {
          ...question,
          id: question.id || uid(), // Ensure each question has an ID
          [getKeyName("question")]: question.question,
          [getKeyName("answer0")]: question.answer0,
          [getKeyName("answer1")]: question.answer1,
          [getKeyName("answer2")]: question.answer2,
          [getKeyName("answer3")]: question.answer3,
        };

        // Only include questionHasPhoto if it's true (Firestore doesn't accept undefined)
        if (question.questionHasPhoto) {
          processedQuestion.questionHasPhoto = true;
        } else {
          // Explicitly delete questionHasPhoto if it's false/undefined (from spread operator)
          delete processedQuestion.questionHasPhoto;
        }

        return processedQuestion;
      });

      scenarios = (scenarios || []).map((scenario) => ({
        ...scenario,
        [getKeyName("scenario")]: scenario.scenario,
        [getKeyName("option0")]: scenario.option0,
        [getKeyName("option1")]: scenario.option1,
        [getKeyName("option2")]: scenario.option2,
        [getKeyName("option3")]: scenario.option3,
      }));

      const rewardValue = type === "song" || type === "betting" || type === "info" ? 0 : parseInt(reward); // Songs, betting and info don't have points, so we set it to 0
      const payload: Record<string, unknown> = {
        reward: rewardValue,
        type,
        questions,
        scenarios,
        title,
        about,
        copy,
        showCorrectAnswers,
      };
      if (supportsIntroPhoto && introPhotoUri) payload.introPhotoUri = introPhotoUri;
      if (type === "info" && audioUri) payload.audioUri = audioUri;
      payload[getKeyName("title")] = title;
      payload[getKeyName("about")] = about;
      payload[getKeyName("copy")] = copy;

      // Use tempChallengeId as the actual challenge ID (important for photo uploads)
      const challengeId = tempChallengeId;
      const challengeDocRef = doc(firestore, `events/${eventId}/challenges/${challengeId}`);

      // Include the ID in the payload
      const payloadWithId = {
        ...payload,
        id: challengeId,
      };

      await setDoc(challengeDocRef, payloadWithId);

      const eventDocRef = doc(firestore, `events/${eventId}`);
      const currentOrder = event?.order || [];
      await updateDoc(eventDocRef, { order: [...currentOrder, challengeId] });

      // Auto-translate asynchronously in background if enabled (not available in trial mode or single language events)
      if (autoTranslate && shouldShowAutoTranslate) {
        try {
          const challengeData = {
            type,
            title,
            about,
            copy,
            questions: type === "quiz" ? questions : undefined,
            scenarios: type === "betting" ? scenarios : undefined,
          };

          const sourceLanguage = locale;

          // Fire and forget - don't await, let it run in background
          autoTranslateChallenge({
            eventId,
            challengeId: challengeId,
            challengeData,
            sourceLanguage,
          })
            .then((result) => {
              const responseData = result?.data as { success: boolean };
              if (responseData?.success) toast(t("translationComplete"), "🌍", "success");
              else toast(t("translationError"), "⚠️", "warning");
            })
            .catch((error) => {
              console.error("Translation failed:", error);
              toast(t("translationError"), "⚠️", "warning");
            });

          // Show immediate feedback that translation started
          toast(t("translating"), "🔄", "info");
        } catch (error) {
          console.error("Translation setup error:", error);
          toast(t("translationError"), "⚠️", "warning");
        }
      }
    } catch (error) {
      console.error(error);
    }
    setUpdating(false);
    toast(t("created"), "👍");
    close();
  };

  const questions: QuizQuestion[] = getValues("questions") || [];
  const scenarios: BettingScenario[] = getValues("scenarios") || [];
  const challengeType = watch("type");
  const onNewQuestionClick = () => {
    if (isTrial) return;
    const newQuestions = [
      ...questions,
      { id: uid(), question: "", answer0: "", answer1: "", answer2: "", answer3: "", correctAnswer: 0 },
    ];
    setValue("questions", newQuestions);
    forceUpdate();
  };

  const handleQuestionPhotoChange = (questionIndex: number, questionHasPhoto: boolean) => {
    const updatedQuestions = [...questions];
    const updatedQuestion = { ...updatedQuestions[questionIndex] };

    if (questionHasPhoto) {
      updatedQuestion.questionHasPhoto = true;
    } else {
      // Remove questionHasPhoto property entirely when photo is deleted (Firestore doesn't accept undefined)
      delete updatedQuestion.questionHasPhoto;
    }

    updatedQuestions[questionIndex] = updatedQuestion;
    setValue("questions", updatedQuestions);
    setActiveQuestionPhotoIndex(undefined);
    forceUpdate();
  };

  const onNewScenarioClick = () => {
    if (isTrial) return;
    const newScenarios = [
      ...scenarios,
      { id: uid(), scenario: "", option0: "", option1: "", option2: "", option3: "" },
    ];
    setValue("scenarios", newScenarios);
    forceUpdate();
  };

  const isQuiz = useMemo(() => challengeType === "quiz", [challengeType]);
  useEffect(() => {
    if (isQuiz) setActiveTab(1);
  }, [challengeType]);

  const isPhoto = useMemo(
    () => challengeType === "photo" || challengeType === "video" || challengeType === "photovideo",
    [challengeType],
  );
  useEffect(() => {
    if (isPhoto) setActiveTab(undefined);
  }, [isPhoto]);

  const isGreeting = useMemo(() => challengeType === "greeting", [challengeType]);
  const isVideo = useMemo(() => challengeType === "video", [challengeType]);
  const isSong = useMemo(() => challengeType === "song", [challengeType]);
  const isBetting = useMemo(() => challengeType === "betting", [challengeType]);
  const isVoting = useMemo(() => challengeType === "voting", [challengeType]);
  const isPhotoVideo = useMemo(() => challengeType === "photovideo", [challengeType]);
  const isOracle = useMemo(() => challengeType === "oracle", [challengeType]);
  const isInfo = useMemo(() => challengeType === "info", [challengeType]);

  // Quiz challenges always support an intro photo; photo/video challenges only for hardcoded events (e.g. eventId === "foobar")
  const challengeIntroPhotosEnabled = ["sloveandthunder", "beatricesmergvakaris"].includes(eventId);
  const supportsIntroPhoto = isQuiz || (challengeIntroPhotosEnabled && isPhoto);

  useEffect(() => {
    if (isGreeting) {
      setValue("title", t("greeting"));
      setValue("about", t("greetingDefaultCopy"));
    }
    if (isSong) {
      setValue("title", t("song"));
      setValue("about", t("songDefaultCopy"));
      setValue("reward", 0); // Songs don't have points
    }
    if (isPhoto || isQuiz || isVideo || isPhotoVideo || isOracle) {
      setValue("title", "");
      setValue("about", "");
    }
    if (isInfo) {
      setValue("title", "");
      setValue("about", "");
      setValue("reward", 0); // Info pages don't have points
    }
    if (isBetting) {
      setValue("title", t("betting"));
      setValue("about", t("bettingDefaultCopy"));
      setValue("reward", 0); // Betting doesn't have points
    }
    if (isVoting) {
      setValue("title", t("voting"));
      setValue("about", t("votingDefaultCopy"));
    }
  }, [isGreeting, isPhoto, isVideo, isQuiz, isSong, isBetting, isVoting, isPhotoVideo, isOracle, isInfo]);

  useEffect(() => {
    if (isOracle) setActiveTab(undefined);
  }, [isOracle]);

  useEffect(() => {
    if (isSong) setActiveTab(undefined);
  }, [isSong]);

  useEffect(() => {
    if (isBetting) setActiveTab(1);
  }, [isBetting]);

  useEffect(() => {
    if (isVoting) setActiveTab(undefined);
  }, [isVoting]);

  const [activeQuestionToRemove, setActiveQuestionToRemove] = useState<number | undefined>(undefined);
  const [activeScenarioToRemove, setActiveScenarioToRemove] = useState<number | undefined>(undefined);

  const onRemove = (index: number) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setValue("questions", newQuestions);
    forceUpdate();
  };

  const onRemoveScenario = (index: number) => {
    const newScenarios = scenarios.filter((_, i) => i !== index);
    setValue("scenarios", newScenarios);
    forceUpdate();
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index > 0) {
      const newOrder = reorder(questions, index, index - 1);
      setValue("questions", newOrder);
    } else if (direction === "down" && index < questions.length - 1) {
      const newOrder = reorder(questions, index, index + 1);
      setValue("questions", newOrder);
    }
    forceUpdate();
  };

  const moveScenario = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index > 0) {
      const newOrder = reorder(scenarios, index, index - 1);
      setValue("scenarios", newOrder);
    } else if (direction === "down" && index < scenarios.length - 1) {
      const newOrder = reorder(scenarios, index, index + 1);
      setValue("scenarios", newOrder);
    }
    forceUpdate();
  };

  const [challenges] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);
  const greetingChallenges = useMemo(() => challenges.filter((c) => c.type === "greeting"), [challenges]);
  const songChallenges = useMemo(() => challenges.filter((c) => c.type === "song"), [challenges]);

  const challengeTypes = useMemo(() => {
    const types = ["photo", "quiz", "video"];

    const isNotCorporateEvent = !event?.isPromo;
    const noGreetingYet = greetingChallenges.length === 0; // we only allow one greeting challenge per event
    if (isNotCorporateEvent && noGreetingYet) types.push("greeting");

    const noSongYet = songChallenges.length === 0; // we only allow one song challenge per event
    if (noSongYet) types.push("song");

    // Voting challenges - can have multiple per event
    types.push("voting");

    const bettingEnabled = event?.mainConfig?.bettingEnabled;
    if (bettingEnabled) types.push("betting");

    const photoVideoEnabled = event?.isPromo; // for now only allowed in Promo events (mostly for Trenkturas/Trailmasters)
    if (photoVideoEnabled) types.push("photovideo");

    // Oracle challenges: text-only, completed in person at a booth. Only for oracle-mode events.
    const oracleEnabled = event?.mainConfig?.oracleMode;
    if (oracleEnabled) types.push("oracle");

    // Info challenges: plain informational text pages (QR deeplink use case). Only for flagged events.
    const infoEnabled = event?.mainConfig?.infoChallengesEnabled;
    if (infoEnabled) types.push("info");

    return types;
  }, [event]);

  const isTrial = event?.isTrial || false;
  const blurChallenge = (challengeType === "video" || challengeType === "photovideo") && isTrial;

  // AI suggestion (magic wand): pre-fills title/about/copy for the selected challenge type
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedTitles, setSuggestedTitles] = useState<string[]>([]);
  const suggestableTypes = ["photo", "quiz", "video", "photovideo"];
  const canSuggest = suggestableTypes.includes(challengeType || challengeTypes[0]);

  const onSuggestClick = async () => {
    if (isTrial || suggesting) return;
    setSuggesting(true);
    try {
      const result = await suggestChallenge({
        eventId,
        challengeType: challengeType || challengeTypes[0],
        locale,
        avoidTitles: suggestedTitles,
      });
      const response = result?.data;
      if (response?.success && response.suggestion) {
        const { title, about, copy } = response.suggestion;
        setValue("title", title);
        setValue("about", about);
        setValue("copy", copy);
        setSuggestedTitles([...suggestedTitles, title]);
        if (isQuiz) setActiveTab(1); // make sure the pre-filled details are visible
        forceUpdate();
      } else {
        toast(t("suggestionError"), "⚠️", "warning");
      }
    } catch (error) {
      console.error("Challenge suggestion failed:", error);
      toast(t("suggestionError"), "⚠️", "warning");
    }
    setSuggesting(false);
  };

  // Check if event has multiple languages enabled
  const enabledLocalesCount = event?.locales ? Object.values(event.locales).filter(Boolean).length : 1;
  const hasMultipleLanguages = enabledLocalesCount > 1;
  const shouldShowAutoTranslate = hasMultipleLanguages;

  return (
    <>
      <ConfirmModal
        show={activeQuestionToRemove !== undefined}
        onCancel={() => setActiveQuestionToRemove(undefined)}
        onConfirm={() => {
          if (activeQuestionToRemove !== undefined) onRemove(activeQuestionToRemove);
        }}
      />
      <ConfirmModal
        show={activeScenarioToRemove !== undefined}
        onCancel={() => setActiveScenarioToRemove(undefined)}
        onConfirm={() => {
          if (activeScenarioToRemove !== undefined) onRemoveScenario(activeScenarioToRemove);
        }}
      />
      <OKModal
        show={showAutoTranslateConfirm}
        messageCopy={t("autoTranslateConfirm")}
        actionCopy={t("enable")}
        onOK={() => {
          setValue("autoTranslate", true);
          setShowAutoTranslateConfirm(false);
          forceUpdate();
        }}
      />
      {activeQuestionPhotoIndex !== undefined && questions[activeQuestionPhotoIndex]?.id && (
        <QuestionPhotoModal
          show={true}
          onClose={() => setActiveQuestionPhotoIndex(undefined)}
          questionId={questions[activeQuestionPhotoIndex].id!}
          challengeId={tempChallengeId}
          questionHasPhoto={!!questions[activeQuestionPhotoIndex].questionHasPhoto}
          onPhotoChange={(questionHasPhoto) => handleQuestionPhotoChange(activeQuestionPhotoIndex, questionHasPhoto)}
        />
      )}
      {showIntroPhotoModal && (
        <IntroPhotoModal
          show={true}
          onClose={() => setShowIntroPhotoModal(false)}
          challengeId={tempChallengeId}
          introPhotoUri={getValues("introPhotoUri")}
          onPhotoChange={(uri) => {
            setValue("introPhotoUri", uri);
            forceUpdate();
          }}
        />
      )}
      {showAudioModal && (
        <AudioModal
          show={true}
          onClose={() => setShowAudioModal(false)}
          challengeId={tempChallengeId}
          audioUri={getValues("audioUri") || undefined}
          onAudioChange={(uri) => {
            setValue("audioUri", uri || "");
            forceUpdate();
          }}
        />
      )}
      <Modal show={true} onCancel={close}>
        <h3 className="text-lg font-bold">{t("create")}</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="h-100 pt-4">
          <div className="mb-4 flex flex-col">
            <label className="label">
              <span className="label-text">{t("challengeType")}</span>
            </label>
            <div className="flex items-center gap-2">
              <select
                className="select-bordered select flex-1"
                defaultValue={challengeTypes[0]}
                {...register("type", { required: true })}
              >
                {challengeTypes.map((type) => {
                  return <option value={type}>{t(type)}</option>;
                })}
              </select>
              {canSuggest && (
                <div
                  className={`${isTrial ? "tooltip-premium" : ""} tooltip tooltip-left text-neutral`}
                  data-tip={isTrial ? t("premiumFeature") : t("suggestChallenge")}
                >
                  <button
                    type="button"
                    onClick={onSuggestClick}
                    disabled={isTrial || suggesting}
                    className={`btn-outline btn-square btn border-base-content border-opacity-20 ${
                      suggesting ? "loading" : ""
                    } ${isTrial ? "btn-disabled" : ""}`}
                  >
                    {!suggesting && "🪄"}
                  </button>
                </div>
              )}
            </div>
          </div>
          <TrialDegrate degrade={blurChallenge}>
            <div className={`${isQuiz || isBetting ? "" : "hidden"}`}>
              <div className="tabs mb-4 mt-8">
                <a
                  className={`tab-bordered tab ${activeTab && activeTab === 1 ? "tab-active" : ""} w-[50%]`}
                  onClick={() => setActiveTab(1)}
                >
                  {t("details")}
                </a>
                <a
                  className={`tab-bordered tab ${activeTab && activeTab === 2 ? "tab-active" : ""} w-[50%]`}
                  onClick={() => setActiveTab(2)}
                >
                  {isQuiz ? t("questions") : t("scenarios")}
                </a>
              </div>
            </div>
            <div className={`${!activeTab || activeTab === 1 ? "" : "hidden"}`}>
              <Input
                label={t("challenge")}
                name="title"
                required
                requiredMessage={t("fieldRequired")}
                errors={errors}
                register={register}
                className="max-w-lg"
              />
              <span className="mt-2" />
              <Textarea
                label={t("about")}
                name="about"
                rows={2}
                errors={errors}
                register={register}
                className="max-w-lg"
              />
              {!isGreeting && !isSong && !isBetting && !isVoting && (
                <Textarea
                  label={t("copy")}
                  name="copy"
                  required
                  rows={4}
                  requiredMessage={t("fieldRequired")}
                  errors={errors}
                  register={register}
                  className="max-w-lg"
                />
              )}
              {!isSong && !isBetting && !isInfo && (
                <Input
                  label={t("reward")}
                  type="number"
                  name="reward"
                  required
                  requiredMessage={t("fieldRequired")}
                  errors={errors}
                  register={register}
                  className="max-w-lg"
                />
              )}
              {challengeType === "quiz" && (
                <div className="form-control mt-4 w-full">
                  <label className="label cursor-pointer">
                    <span className="label-text mr-3 text-sm">{t("showCorrectAnswers")}</span>
                    <input
                      type="checkbox"
                      className="toggle-primary toggle"
                      defaultChecked={getValues(`showCorrectAnswers`)}
                      onClick={(e) => {
                        const value = (e.target as any).checked;
                        setValue(`showCorrectAnswers`, value);
                        forceUpdate();
                      }}
                    />
                  </label>
                </div>
              )}
              {supportsIntroPhoto && (
                <div className="form-control mt-4 w-full">
                  <button
                    type="button"
                    onClick={() => setShowIntroPhotoModal(true)}
                    className={`btn-outline btn-sm btn ${getValues("introPhotoUri") ? "btn-primary" : "btn-secondary"}`}
                  >
                    {getValues("introPhotoUri") ? t("introPhotoChange") : t("introPhotoAdd")}
                  </button>
                </div>
              )}
              {isInfo && (
                <div className="form-control mt-4 w-full">
                  <button
                    type="button"
                    onClick={() => setShowAudioModal(true)}
                    className={`btn-outline btn-sm btn ${getValues("audioUri") ? "btn-primary" : "btn-secondary"}`}
                  >
                    {getValues("audioUri") ? t("audioChange") : t("audioAdd")}
                  </button>
                </div>
              )}
            </div>
            <div className={`${activeTab === 2 && isQuiz ? "" : "hidden"}`}>
              <div>
                {questions.map(({ question, id, questionHasPhoto }, i) => {
                  const key = id ?? question ?? i;
                  return (
                    <div key={key}>
                      <Question
                        moveItem={moveItem}
                        isFirst={i === 0}
                        isLast={i === questions.length - 1}
                        updateCorrectAnswer={(correctAnswer) => {
                          setValue(`questions.${i}.correctAnswer`, correctAnswer);
                          forceUpdate();
                        }}
                        questionIndex={i}
                        correctAnswer={getValues(`questions.${i}.correctAnswer`)}
                        register={register}
                        errors={errors}
                        setActiveQuestionToRemove={(index) => setActiveQuestionToRemove(index)}
                        onPhotoClick={() => setActiveQuestionPhotoIndex(i)}
                        questionHasPhoto={!!questionHasPhoto}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-center">
                <div className="tooltip-premium tooltip text-neutral" data-tip={t("premiumFeature")}>
                  <button
                    onClick={() => onNewQuestionClick()}
                    className={`btn-outline btn-primary btn my-6 h-10 w-28 text-lg ${isTrial ? "btn-disabled" : ""}`}
                    disabled={isTrial}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <div className={`${activeTab === 2 && isBetting ? "" : "hidden"}`}>
              <div>
                {scenarios.map(({ scenario, id }, i) => {
                  const key = id ?? scenario ?? i;
                  return (
                    <div key={key}>
                      <BettingScenario
                        moveScenario={moveScenario}
                        isFirst={i === 0}
                        isLast={i === scenarios.length - 1}
                        scenarioIndex={i}
                        register={register}
                        errors={errors}
                        setActiveScenarioToRemove={(index) => setActiveScenarioToRemove(index)}
                        scenario={scenarios[i]}
                        showResolveButton={false}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-center">
                <div className="tooltip-premium tooltip text-neutral" data-tip={t("premiumFeature")}>
                  <button
                    onClick={() => onNewScenarioClick()}
                    className={`btn-outline btn-primary btn my-6 h-10 w-28 text-lg ${isTrial ? "btn-disabled" : ""}`}
                    disabled={isTrial}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <div>
              {shouldShowAutoTranslate && (
                <div className="form-control mt-4 w-full">
                  <div
                    className={`${isTrial ? "tooltip-premium tooltip text-neutral" : ""}`}
                    data-tip={isTrial ? t("premiumFeature") : ""}
                  >
                    <label className="label cursor-pointer">
                      <span className="label-text mr-3 text-sm">{t("autoTranslate")}</span>
                      <input
                        type="checkbox"
                        className={`toggle-primary toggle ${isTrial ? "toggle-disabled" : ""}`}
                        defaultChecked={false}
                        disabled={isTrial}
                        onClick={(e) => {
                          if (!isTrial) {
                            const value = (e.target as any).checked;
                            if (value) {
                              setShowAutoTranslateConfirm(true);
                            } else {
                              setValue("autoTranslate", false);
                            }
                            forceUpdate();
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
              <div className="mt-8 mb-2 flex flex-col">
                <button
                  type="submit"
                  disabled={updating}
                  className={`inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-base-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${
                    updating ? "loading" : ""
                  }`}
                >
                  {t("save")}
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50  sm:w-auto"
                  onClick={close}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </TrialDegrate>
        </form>
      </Modal>
    </>
  );
};
